import React from "react";
import PropTypes from "prop-types";
import { Grid, IconButton, Tooltip, FormHelperText } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DateTimeInput from "components/MDDateTimeInput";
// Receipt page theme (shared)
import {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
  ranuTextButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";
import FormField from "layouts/receipt/components/FormField";
import { EVENT_FIELDS } from "./eventFromUtils";

const tileSx =
  (isNew = false) =>
  ({ palette, borders }) => ({
    position: "relative",
    display: "block",
    width: "100%",
    height: 140,
    overflow: "hidden",
    textDecoration: "none",
    borderRadius: borders.borderRadius.lg,
    backgroundColor: palette.light.main,
    border: isNew
      ? `2px solid ${palette.secondary.main}`
      : `${borders.borderWidth[1]} solid ${borders.borderColor}`,
  });

// Back button next to the page badge: maroon outline and text, arrow icon on the left,
// gold on hover (same as the Devotee / Blog add pages)
const backButtonSx = ({ palette }) => ({
  color: palette.primary.main,
  borderColor: palette.primary.main,
  backgroundColor: "transparent",
  borderRadius: "10px",
  boxShadow: "none",
  boxSizing: "border-box",
  height: { xs: "2.25rem", md: "2.5rem" },
  minHeight: { xs: "2.25rem", md: "2.5rem" },
  px: 2.5,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  gap: 0.75,
  transition: "background-color .3s ease, color .3s ease, border-color .3s ease",
  "&:hover": {
    color: palette.secondary.main,
    borderColor: palette.secondary.main,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// Page heading (badge + short description), same as Blog / Devotee.
// Pass `onBack` to show the Back button right beside the badge.
export const EventFormHeader = ({ icon, label, description, onBack }) => (
  <SectionHeader
    icon={icon}
    label={label}
    description={description}
    startAction={
      onBack ? (
        <MDButton
          type="button"
          variant="outlined"
          color="primary"
          sx={backButtonSx}
          startIcon={<ArrowBackIcon sx={{ fontSize: "1.1rem !important" }} />}
          onClick={onBack}
        >
          Back
        </MDButton>
      ) : undefined
    }
  />
);

EventFormHeader.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  onBack: PropTypes.func,
};

EventFormHeader.defaultProps = {
  description: "",
  onBack: undefined,
};

// Bottom bar.
// - Without `onBack`: Cancel + Save only, bottom right on desktop, centered on mobile
//   (use this together with `onBack` on EventFormHeader).
// - With `onBack`: the old layout, Back on the left and Cancel + Save on the right.
// - `formId`: set it when this bar is rendered OUTSIDE the <form>, so Save still submits it.
export const EventFormActions = ({ onBack, onCancel, submitLabel, submitDisabled, formId }) => {
  const { accentName } = useAccent();

  return (
    <MDBox
      display="flex"
      justifyContent={onBack ? "space-between" : { xs: "center", md: "flex-end" }}
      alignItems="center"
      flexWrap="wrap"
      gap={1}
    >
      {onBack && (
        <MDButton
          variant="text"
          color={accentName}
          sx={ranuTextButtonSx}
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
        >
          Back
        </MDButton>
      )}

      <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={1.5}>
        <MDButton
          type="button"
          variant="outlined"
          color="primary"
          sx={ranuOutlinedButtonSx}
          onClick={onCancel}
        >
          Cancel
        </MDButton>
        <MDButton
          type="submit"
          form={formId}
          color="primary"
          sx={ranuButtonSx}
          disabled={submitDisabled}
        >
          {submitLabel}
        </MDButton>
      </MDBox>
    </MDBox>
  );
};

EventFormActions.propTypes = {
  onBack: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  submitLabel: PropTypes.string.isRequired,
  submitDisabled: PropTypes.bool,
  formId: PropTypes.string,
};

EventFormActions.defaultProps = {
  onBack: undefined,
  submitDisabled: false,
  formId: undefined,
};

// Title + description take the full row, the two dates sit side by side on desktop
export const EventDetailsFields = ({ eventData, onChange, onBlur, getVisibleError }) => (
  <Grid container spacing={2}>
    {EVENT_FIELDS.map((field) => {
      const { name, label, type, required, multiline, minFrom, maxFrom } = field;
      const error = getVisibleError(field);

      return (
        <FormField
          key={name}
          name={label.split(" ").join("_")}
          required={required}
          full={name === "event_title" || !!multiline}
        >
          <DateTimeInput
            name={name}
            type={type || "text"}
            multiline={multiline}
            rows={multiline ? 6 : undefined}
            placeholder={type === "date" ? undefined : `Enter ${label.toLowerCase()}`}
            fullWidth
            value={eventData[name] || ""}
            onChange={onChange}
            onBlur={() => onBlur(name)}
            error={!!error}
            inputProps={{
              min: eventData[minFrom] || undefined,
              max: eventData[maxFrom] || undefined,
            }}
          />
          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormField>
      );
    })}
  </Grid>
);

EventDetailsFields.propTypes = {
  eventData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  getVisibleError: PropTypes.func.isRequired,
};

// Native file input (same accept list and behaviour as before), themed
export const MediaPicker = ({ onChange }) => (
  <MDBox
    component="input"
    type="file"
    name="image"
    multiple
    accept="image/*"
    onChange={onChange}
    sx={({ palette, borders }) => ({
      width: "100%",
      padding: "10px",
      cursor: "pointer",
      fontSize: 14,
      color: palette.text.main,
      border: `1px dashed ${palette.secondary.main}`,
      borderRadius: borders.borderRadius.lg,
      "&::file-selector-button": {
        marginRight: "12px",
        padding: "6px 14px",
        border: "none",
        borderRadius: borders.borderRadius.md,
        cursor: "pointer",
        color: palette.white.main,
        backgroundColor: palette.primary.main,
        transition: "background-color .3s ease",
      },
      "&::file-selector-button:hover": { backgroundColor: palette.secondary.main },
    })}
  />
);

MediaPicker.propTypes = {
  onChange: PropTypes.func.isRequired,
};

// items: [{ key, src, href, alt, onDelete }]
// isNew: highlights images that are selected but not uploaded yet (gold border)
export const MediaGrid = ({ items, isNew }) => (
  <Grid container spacing={2}>
    {items.map(({ key, src, href, alt, onDelete }) => (
      <Grid item key={key} xs={6} sm={4} md={3}>
        <MDBox sx={tileSx(isNew)}>
          <img
            src={src}
            alt={alt}
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />

          <MDBox
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Tooltip title="Open in new tab">
              <IconButton
                size="small"
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.65)",
                  color: "#ffffff",
                  padding: "4px",
                }}
              >
                <OpenInNewIcon style={{ fontSize: "14px" }} />
              </IconButton>
            </Tooltip>

            {/* Round "x" button, same as the Blog images */}
            <MDBox
              component="button"
              type="button"
              onClick={onDelete}
              aria-label="Remove image"
              sx={({ palette }) => ({
                width: 26,
                height: 26,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                borderRadius: "50%",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 700,
                color: palette.white.main,
                backgroundColor: palette.error.main,
                transition: "background-color .3s ease",
                "&:hover": { backgroundColor: palette.primary.main },
              })}
            >
              ✕
            </MDBox>
          </MDBox>
        </MDBox>
      </Grid>
    ))}
  </Grid>
);

MediaGrid.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      src: PropTypes.string,
      href: PropTypes.string,
      alt: PropTypes.string,
      onDelete: PropTypes.func.isRequired,
    })
  ).isRequired,
  isNew: PropTypes.bool,
};

MediaGrid.defaultProps = {
  isNew: false,
};
